import React from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  Input,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { FiBell } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AppBar } from "../../components/mobileUi";
import { PRIMARY, BORDER, TEXT } from "../../components/requestUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  createLeaveRequestApi,
  getLeaveTypesApi,
  getRemainingDaysApi,
} from "../../api/leave.api";
import { extractErrorMessage } from "../../api/client";
import { dayKey } from "../../utils/format";
import { EMP_BASE_PATH } from "../../config";

const BLUE_GRADIENT = "linear(to-r, #68A2FF, #2168DA)";

const Label = ({ children, required }) => (
  <Text fontSize="13px" fontWeight={600} mb="8px" color={TEXT}>
    {children}
    {required && <Text as="span" color="#EF2E14"> *</Text>}
  </Text>
);

// Bộ chọn số ngày (- value +) khớp app
const Stepper = ({ value, onChange }) => (
  <Flex border="1px solid" borderColor={BORDER} borderRadius="8px" overflow="hidden" h="42px">
    <Flex as="button" type="button" w="44px" align="center" justify="center" onClick={() => onChange(Math.max(0, +(value - 0.5).toFixed(1)))} color={PRIMARY} fontSize="22px" borderRight="1px solid" borderColor={BORDER}>
      −
    </Flex>
    <Flex flex="1" align="center" justify="center" fontSize="15px" fontWeight={600} color={TEXT}>
      {value}
    </Flex>
    <Flex as="button" type="button" w="44px" align="center" justify="center" onClick={() => onChange(+(value + 0.5).toFixed(1))} color={PRIMARY} fontSize="22px" borderLeft="1px solid" borderColor={BORDER}>
      +
    </Flex>
  </Flex>
);

const LeaveCreate = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      leaveTypeCode: "",
      startDate: dayKey(),
      daysRequested: 1,
      reason: "",
    },
  });

  const leaveTypeCode = watch("leaveTypeCode");

  const typesQuery = useQuery({
    queryKey: ["emp", "leave-types"],
    queryFn: getLeaveTypesApi,
  });

  const remainingQuery = useQuery({
    queryKey: ["emp", "leave-remaining", leaveTypeCode],
    queryFn: () => getRemainingDaysApi(leaveTypeCode),
    enabled: !!leaveTypeCode,
  });

  const createMutation = useMutation({
    mutationFn: createLeaveRequestApi,
    onSuccess: () => {
      toast.success("Tạo đơn nghỉ phép thành công");
      queryClient.invalidateQueries({ queryKey: ["emp", "leave-list"] });
      navigate(`${EMP_BASE_PATH}/nghi-phep`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Tạo đơn thất bại")),
  });

  const onSubmit = (v) => {
    if (!Number(v.daysRequested) || Number(v.daysRequested) <= 0) {
      toast.error("Số ngày nghỉ là trường bắt buộc");
      return;
    }
    createMutation.mutate({
      startDate: v.startDate,
      leaveTypeCode: v.leaveTypeCode,
      reason: v.reason.trim(),
      daysRequested: Number(v.daysRequested),
    });
  };

  const types = typesQuery.data?.items || typesQuery.data || [];
  const remaining = remainingQuery.data;
  const remainLeave = remaining?.remainingDaysInYear ?? remaining?.paidLeaveBalance ?? 0;
  const remainComp = remaining?.compensatoryLeaveBalance ?? 0;

  const formEl = (
      <form onSubmit={handleSubmit(onSubmit)} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Lý do */}
        <FormControl mb="18px" isInvalid={!!errors.reason}>
          <Label required>Lí do</Label>
          <Controller
            control={control}
            name="reason"
            rules={{ required: "Vui lòng nhập lý do" }}
            render={({ field }) => (
              <Textarea {...field} placeholder="Nhập lí do xin nghỉ..." borderColor={BORDER} borderRadius="8px" rows={4} fontSize="14px" />
            )}
          />
          {errors.reason && <Text fontSize="12px" color="red.500" mt="4px">{errors.reason.message}</Text>}
        </FormControl>

        {/* Hình thức nghỉ (radio) */}
        <FormControl mb="18px" isInvalid={!!errors.leaveTypeCode}>
          <Label required>Hình thức nghỉ</Label>
          <Controller
            control={control}
            name="leaveTypeCode"
            rules={{ required: "Vui lòng chọn hình thức nghỉ" }}
            render={({ field }) => (
              <Box border="1px solid" borderColor={BORDER} borderRadius="10px" px="14px">
                {types.map((t, i) => {
                  const checked = field.value === t.code;
                  return (
                    <Flex
                      key={t.code}
                      as="button"
                      type="button"
                      w="100%"
                      align="center"
                      gap="12px"
                      py="14px"
                      borderBottom={i === types.length - 1 ? "none" : "1px solid #EEF1F6"}
                      onClick={() => field.onChange(t.code)}
                      textAlign="left"
                    >
                      <Flex w="20px" h="20px" borderRadius="full" border="2px solid" borderColor={checked ? PRIMARY : "#C8CCD6"} align="center" justify="center" flexShrink={0}>
                        {checked && <Box w="10px" h="10px" borderRadius="full" bg={PRIMARY} />}
                      </Flex>
                      <Text fontSize="14px" color={TEXT}>{t.name}</Text>
                    </Flex>
                  );
                })}
              </Box>
            )}
          />
          {errors.leaveTypeCode && <Text fontSize="12px" color="red.500" mt="4px">{errors.leaveTypeCode.message}</Text>}
        </FormControl>

        {/* Ngày bắt đầu + Số ngày */}
        <Flex gap="12px" mb="18px">
          <FormControl flex="1">
            <Label required>Ngày bắt đầu nghỉ</Label>
            <Input type="date" borderColor={BORDER} borderRadius="8px" h="48px" fontSize="14px" {...register("startDate", { required: true })} />
          </FormControl>
          <FormControl flex="1">
            <Label>Số ngày nghỉ</Label>
            <Controller
              control={control}
              name="daysRequested"
              render={({ field }) => <Stepper value={Number(field.value) || 0} onChange={field.onChange} />}
            />
          </FormControl>
        </Flex>

        {/* Banner số ngày còn lại */}
        <Flex align="center" gap="8px" mb="10px">
          <Box as={FiBell} color="#F79D0D" fontSize="18px" />
          <Text fontSize="14px" color={TEXT}>
            Ngày nghỉ phép còn lại: <Text as="span" color={PRIMARY} fontWeight={700}>{remainLeave} ngày</Text>
          </Text>
        </Flex>
        <Flex align="center" gap="8px" mb="20px">
          <Box as={FiBell} color="#F79D0D" fontSize="18px" />
          <Text fontSize="14px" color={TEXT}>
            Ngày nghỉ bù còn lại: <Text as="span" color={PRIMARY} fontWeight={700}>{remainComp} ngày</Text>
          </Text>
        </Flex>

        <Flex gap="10px" mt="auto" pt="16px">
          <Button
            flex="1"
            h="48px"
            variant="outline"
            borderColor={PRIMARY}
            color={PRIMARY}
            borderRadius="10px"
            _hover={{ bg: "#EAF2FF" }}
            onClick={() => navigate(`${EMP_BASE_PATH}/nghi-phep`)}
          >
            Hủy
          </Button>
          <Button
            flex="1"
            h="48px"
            type="submit"
            isLoading={createMutation.isPending}
            bgGradient={BLUE_GRADIENT}
            color="white"
            borderRadius="10px"
            _hover={{ bgGradient: BLUE_GRADIENT, opacity: 0.92 }}
          >
            Gửi quản lý
          </Button>
        </Flex>
      </form>
  );

  // ===================== Bản WEB (desktop) =====================
  // Giữ nguyên form/validate/mutation, chỉ đặt trong khung trang kiểu admin.
  if (isDesktop) {
    return (
      <DesktopPage
        title="Tạo đơn xin nghỉ phép"
        right={<DesktopBack onClick={() => navigate(`${EMP_BASE_PATH}/nghi-phep`)} />}
      >
        <DesktopPanel p="24px">
          {formEl}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title="Tạo đơn xin nghỉ phép" onBack={() => navigate(`${EMP_BASE_PATH}/nghi-phep`)} />

      <Box px="16px" pt="16px" pb="16px" flex="1" display="flex" flexDirection="column">
        {formEl}
      </Box>
    </Box>
  );
};

export default LeaveCreate;
